import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Wallet as WalletIcon } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StripePayment from "../components/StripePayment";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtCents, fmtDateTime, fmtMoney, setPageTitle } from "../lib/format";

const FILTERS = [["all", "All"], ["earnings", "Earnings"], ["bookings", "Bookings"], ["topups", "Top-ups"], ["cashouts", "Cashouts"], ["refunds", "Refunds"]];

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);
  const [topup, setTopup] = useState("");
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(null);
    api.wallet().then(setWallet).catch((e) => setError(e.message));
    api.walletTransactions(filter).then(setTxns).catch(() => setTxns([]));
  };
  useEffect(() => { setPageTitle("Wallet"); load(); }, [filter]); // eslint-disable-line

  const startTopup = async () => {
    const cents = Math.round(parseFloat(topup) * 100);
    if (!cents || cents < 100) { toast.error("Minimum top-up is $1.00"); return; }
    setBusy(true);
    try {
      const r = await api.walletTopup(cents);
      setPay({ client_secret: r.client_secret, publishable_key: r.publishable_key, amount_cents: r.amount_cents });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="wallet-page">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Money</p>
        <h1 className="type-display mt-3">Wallet</h1>
        {error ? <div className="mt-8"><ErrorState body={error} onRetry={load} /></div> : !wallet ? <PageLoading label="Loading wallet…" /> : (
          <>
            <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="wallet-balances">
              <div className="card-dark p-6">
                <p className="label-dark">Available balance</p>
                <p className="font-display font-extrabold text-4xl" data-testid="wallet-balance">{fmtCents(wallet.balance_cents)}</p>
              </div>
              <div className="card-dark p-6">
                <p className="label-dark">Pending</p>
                <p className="font-display font-extrabold text-4xl text-zinc-400" data-testid="wallet-pending">{fmtCents(wallet.pending_cents)}</p>
              </div>
            </div>

            <div className="card-dark p-6 mt-6" data-testid="topup-card">
              <h2 className="font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-[#FF3B45]" /> Add funds</h2>
              {!pay ? (
                <div className="mt-4 flex gap-3">
                  <input data-testid="topup-amount" type="number" min="1" step="1" value={topup} onChange={(e) => setTopup(e.target.value)}
                         className="input-dark flex-1" placeholder="Amount in USD" aria-label="Top-up amount" />
                  <button onClick={startTopup} disabled={busy} data-testid="topup-start-btn" className="btn-primary text-sm">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Top up
                  </button>
                </div>
              ) : (
                <div className="mt-5" data-testid="topup-payment">
                  <StripePayment
                    clientSecret={pay.client_secret}
                    publishableKey={pay.publishable_key}
                    amountLabel={fmtMoney(pay.amount_cents / 100)}
                    onSuccess={() => { toast.success("Funds added"); setPay(null); setTopup(""); load(); }}
                    onError={(m) => toast.error(m)} />
                  <p className="mt-2 text-xs text-zinc-500">Publishable key is provided by the server at payment time; card data never touches MyRedPark.</p>
                </div>
              )}
            </div>

            <div className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2"><WalletIcon className="w-4 h-4 text-[#FF3B45]" /> Transactions</h2>
              </div>
              <div className="flex gap-2 overflow-auto mb-4" role="tablist" aria-label="Transaction filter">
                {FILTERS.map(([v, l]) => (
                  <button key={v} role="tab" aria-selected={filter === v} data-testid={`txn-filter-${v}`} onClick={() => setFilter(v)}
                          className={`px-4 py-2 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors ${filter === v ? "bg-[#E50914] border-[#E50914]" : "bg-[#1F2328] border-[#27272A] text-zinc-300"}`}>{l}</button>
                ))}
              </div>
              {txns === null ? <PageLoading label="Loading transactions…" /> : txns.length === 0 ? (
                <EmptyState title="No transactions" body="Wallet activity will appear here." />
              ) : (
                <div className="card-dark divide-y divide-[#27272A]" data-testid="txn-list">
                  {txns.map((t) => (
                    <div key={t.txn_id} className="p-4 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{t.description || t.type}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">{fmtDateTime(t.created_at)} · {t.status}</p>
                      </div>
                      <span className={`font-display font-bold text-sm shrink-0 ${t.amount_cents >= 0 ? "text-emerald-400" : "text-zinc-300"}`}>
                        {t.amount_cents >= 0 ? "+" : "−"}{fmtCents(Math.abs(t.amount_cents))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
